import { Location } from '@angular/common';
import { Observable, of } from 'rxjs';

import * as moment from 'moment';
import { PlotOptions, SeriesOptionsType, SeriesScatterOptions, TooltipFormatterContextObject } from 'highcharts';

import { ActivatedRoute, Router } from '@angular/router';
import { AppTeamMessageService } from '../../core-ui/utils/app-team-message.service';
import { AppTeamUtil } from '../../core/app-team-util';
import { BroadcastService } from '../../core/services/broadcast.service';
import { NotificationManagerService } from '../../api/notification/notification-manager.service';
import { OrganizationManagerService } from '../../api/organization/organization-manager.service';
import { ReportManagerService } from '../../api/reports/report-manager.service';
import { SectionHostBase } from '../../core-ui/section-host/section-host-base';
import { LocalStorageManagerService } from '../../core/services/local-storage-manager.service';
import { Injector } from '@angular/core';
import { NotificationSendInfo } from '../../api/notification/notification-send-info.model';

export class ReportBase extends SectionHostBase
{
	// private static BASE_THEME_COLOR = '#000000';
	// private static REPORT_SECONDARY_COLOR = '#b3b3b3';
	private static BASE_THEME_COLOR = '#094399';
	private static REPORT_SECONDARY_COLOR = '#3e87f4';
	private static REPORT_TERTIARY_COLOR = '#b7d2fb';
	static REPORT_OPTIONS = {
		GRAPH_COLORS: [ ReportBase.BASE_THEME_COLOR, ReportBase.REPORT_SECONDARY_COLOR, ReportBase.REPORT_TERTIARY_COLOR ],
		NOTIFICATIONS_BUBBLE_COLOR: '#FF0000FF',
		NOTIFICATIONS_Y_AXIS_OPTIONS: { min: 0, max: 1, startOnTick: false, endOnTick: false, visible: false },
		BUBBLE_OPTIONS: { minSize: 8, maxSize: 8 }
	}
	// private static BASE_THEME_COLOR = '#14B8A6';
	// private static REPORT_SECONDARY_COLOR = '#3db2c0';
	// static REPORT_COLORS = {
	//     GRAPH_COLORS: [ ReportManagerService.BASE_THEME_COLOR, ReportBase.REPORT_SECONDARY_COLOR ],
	//     HIGHLIGHT_COLORS: [ AppTeamUtil.lightenOrDarkenColor(ReportManagerService.BASE_THEME_COLOR, .3),
	//         AppTeamUtil.lightenOrDarkenColor(ReportBase.REPORT_SECONDARY_COLOR, .3)
	//     ]
	// }

	protected displayNotificationsOnChart = false;
	busy = false;
	noDataAvailable?: boolean;
	noDataAvailableString = 'There is no data for the requested time period.';

	private _dateRange!: Date[];
	protected get dateRange(): Date[] { return this._dateRange; }
	protected set dateRange(value: Date[]) { this._dateRange = value; this.localStorage.reportDates = value; }

	protected localStorage!: LocalStorageManagerService;
	private series?: SeriesOptionsType;

	constructor(protected override router: Router,
	            protected override activatedRoute: ActivatedRoute,
	            protected override location: Location,
	            protected override broadcastService: BroadcastService,
	            protected override appTeamMessageService: AppTeamMessageService,
	            protected notificationManager: NotificationManagerService,
	            protected orgManager: OrganizationManagerService,
	            protected reportManager: ReportManagerService) {
		super(router, activatedRoute, location, broadcastService, appTeamMessageService);

		const endDate = moment().endOf('day');
		const startDate = moment(endDate).subtract(6, 'months');
		const i = Injector.create({ providers: [{ provide: LocalStorageManagerService, useClass: LocalStorageManagerService }]});
		this.localStorage = i.get(LocalStorageManagerService);
		this._dateRange = this.localStorage.reportDates ?? [ startDate.toDate(), endDate.toDate() ];
	}

	// get plotOptions(): PlotOptions { return this.displayNotificationsOnChart ? { bubble: ReportBase.REPORT_OPTIONS.BUBBLE_OPTIONS } : {}; }
	get plotOptions(): PlotOptions { return {}; }

	getChartDataSeries(): SeriesOptionsType
	{
		if (!this.displayNotificationsOnChart) return <SeriesScatterOptions>{};
		return this.notificationsReportSeries;
	}

	protected setSpecialDateRange(startDate: Date, endDate: Date): void
	{
		this._dateRange = [ startDate, endDate ];
	}

	protected getTooltipText(contextObject: TooltipFormatterContextObject): string | undefined
	{
		if (!this.displayNotificationsOnChart || contextObject.series.name !== this.series?.name) return undefined;
		// @ts-ignore
		return this.reportTooltipDate(contextObject.x, this.dateRange[0], this.dateRange[1]) + `<br/><br/>${contextObject.series.name}: ${contextObject.point.notificationCount}`;
	}

	protected prepareToLoadData(): Observable<any>
	{
		if (this.dateRange[0] === undefined || this.dateRange[1] === undefined) throw 'Dates undefined';
		if (!this.displayNotificationsOnChart) return of(false);
		return this.notificationManager.getNotifications();
	}

	protected reportTooltipDate(date: number | string | undefined): string
	{
		const dateFormat = this.dateRange[0]?.getFullYear() !== this.dateRange[1]?.getFullYear() ? 'ddd, MMM-DD-YYYY' : 'ddd, MMM-DD';
		return moment(date).format(dateFormat);
	}

	get notificationsReportSeries(): SeriesOptionsType
	{
		if (this.dateRange[0] === undefined || this.dateRange[1] === undefined) throw 'Dates undefined';

		const start = moment(this.dateRange[0]).startOf('day').toDate();
		const end = moment(this.dateRange[1]).endOf('day').toDate();
		const sendInfos = this.notificationManager.notificationsInDateRange(this.dateRange[0], this.dateRange[1]);
		const groups = AppTeamUtil.groupBy(sendInfos, (si: NotificationSendInfo) => moment(si.SentDateTime).startOf('day').toDate().valueOf());
		const notificationCountByDate = Array.from(groups.entries()).map(group => ({ date: group[0], count: group[1].length}));
		this.series = {
			name: 'Notifications',
			type: 'scatter',
			yAxis: 1,
			color: ReportBase.REPORT_OPTIONS.NOTIFICATIONS_BUBBLE_COLOR,
			data: notificationCountByDate.map((d: any) => ({ x: d.date, y: 0.005, notificationCount: d.count })) // To display it at the bottom of the chart
		};
		return this.series;
	}
}
